import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import RadioGroup, { NO_VALUE } from "components/molecules/RadioGroup";
import CheckboxGroup from "components/molecules/CheckboxGroup";
import InputSearchField from "components/molecules/InputSearchField";
import classnames from "classnames";

const filterDataHasChanged = (dataArray1, dataArray2) => {
  if (dataArray1.length !== dataArray2.length) {
    return true;
  }

  return dataArray1.some((data1, index) => {
    const data2 = dataArray2[index];
    return data1.label !== data2.label || data1.value !== data2.value;
  });
};

const Panel = ({
  isMultiSelectFilter = false,
  name,
  data = [],
  onFilterValueChange,
  selectedValues = [],
  textFilterThreshold = 16,
  className,
  noHeader = false,
}) => {
  const [filterText, setFilterText] = useState("");
  const [prevData, setPrevData] = useState(data);

  useEffect(() => {
    if (filterDataHasChanged(data, prevData)) {
      setFilterText("");
      setPrevData(data);
    }
  }, [data, prevData]);

  const handleFilterTextInput = (text) => {
    setFilterText(text);
  };

  const handleCheckboxChange = (data) => {
    onFilterValueChange({
      name,
      values: data.values,
    });
  };

  const handleRadioChange = (data) => {
    onFilterValueChange({
      name,
      values: data.value === NO_VALUE ? [] : [data.value],
    });
  };

  const hasPanelHeader = () => {
    return (
      textFilterThreshold >= 0 && data.length > textFilterThreshold && !noHeader
    );
  };

  const getFilteredData = () => {
    if (!filterText) return data;
    return data.filter((entry) =>
      entry.label.toLowerCase().includes(filterText.toLowerCase()),
    );
  };

  const getInputData = () => {
    const filteredData = getFilteredData();
    const counterClassName = isMultiSelectFilter
      ? "sal-checkbox__counter"
      : "sal-radio__counter";
    const labelClassName = isMultiSelectFilter
      ? "sal-checkbox__label"
      : "sal-radio__label";

    return filteredData.map((entry) => {
      const label = (
        <Fragment>
          <span key={1} className={labelClassName}>
            {entry.label}
          </span>
          {entry.count !== 0 && (
            <span key={2} className={counterClassName} aria-hidden="true">
              ({entry.count})
            </span>
          )}
        </Fragment>
      );

      return {
        label,
        value: entry.value,
      };
    });
  };

  const componentBaseClass = "sal-panel";
  const componentClassName = classnames(
    componentBaseClass,
    {
      [`${componentBaseClass}--has-header`]: hasPanelHeader(),
    },
    className,
  );

  return (
    <div className={componentClassName}>
      {hasPanelHeader() && (
        <div className={`${componentBaseClass}__header`}>
          <InputSearchField
            onFilterTextInput={handleFilterTextInput}
            filterText={filterText}
            className={`${componentBaseClass}__search`}
            noSubmitOnEnter
          />
        </div>
      )}

      {isMultiSelectFilter ? (
        <CheckboxGroup
          name={name}
          checkboxes={getInputData()}
          selectedValues={selectedValues}
          onValuesChange={handleCheckboxChange}
        />
      ) : (
        <RadioGroup
          name={name}
          resetOnRadioClick
          radios={getInputData()}
          selectedValue={selectedValues.length > 0 ? selectedValues[0] : ""}
          onValueChange={handleRadioChange}
        />
      )}
    </div>
  );
};

Panel.propTypes = {
  isMultiSelectFilter: PropTypes.bool,
  name: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      count: PropTypes.number,
    }),
  ).isRequired,
  onFilterValueChange: PropTypes.func.isRequired,
  selectedValues: PropTypes.array,
  textFilterThreshold: PropTypes.number,
  className: PropTypes.string,
  noHeader: PropTypes.bool,
};

export default Panel;
